import { i18n } from '../i18n/i18n'

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { startCase } from 'lodash'
import routes from '../routes/routes'
import timezoneApi, { TimezoneData } from '../apis/timezone.api'
import trialApi, { TrialCreateRequest, TrialType } from '../apis/trial.api'
import {
  ReadCallbackData as GooglePlaceReadCallbackData
} from '../apis/googlePlace.api'
import alertService from '../services/alert.service'
import timezoneService from '../services/timezone.service'
import trialService from '../services/trial.service'
import Card from '../components/Card'
import Heading from '../components/Heading'
import Logo from '../components/Logo'
import Row from '../components/Row/Row'
import InputText from '../components/InputText'
import Select from '../components/Select'
import CardButton from '../components/CardButton'
import AddressSearchPicker from '../components/AddressSearchPicker'
import SandboxBadge from '../components/SandboxBadge'

interface Props extends RouteComponentProps {
    type: TrialType
}

interface State {
    fullName: string
    role: string
    username: string
    email: string
    phone: string
    organizationName: string
    timezone: string
    address: string
    city: string
    state: string
    zip: string
    timezones: TimezoneData[]
}

class TrialForm extends Component<Props, State> {
  initialState: State = {
    fullName: '',
    role: '',
    username: '',
    email: '',
    phone: '',
    organizationName: '',
    timezone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    timezones: []
  }

  constructor (props: Props) {
    super(props)
    this.state = this.initialState
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleTrialClick = this.handleTrialClick.bind(this)
  }

  get subHeading () {
    const { type } = this.props
    if (type !== 'customLabel' && type !== 'productionCustomLabel') return
    const heading = `${i18n('Free Account Request for ')}${startCase(type)} Builder`
    return <Heading
            size="small"
            text={heading}
            className='mb-2'/>
  }

  get roleSelectOptions () {
    return trialService.roleSelectOptions()
  }

  get timezoneSelectOptions () {
    return timezoneService.selectOptions(this.state.timezones)
  }

  handleInputChange (name: string, value: string) {
    if (name === 'fullName') this.setState({ fullName: value })
    if (name === 'role') this.setState({ role: value })
    if (name === 'username') this.setState({ username: value })
    if (name === 'email') this.setState({ email: value })
    if (name === 'phone') this.setState({ phone: value })
    if (name === 'organizationName') this.setState({ organizationName: value })
    else if (name === 'timezone') this.setState({ timezone: value })
  }

  handleTrialClick () {
    const { type } = this.props
    const { fullName, role, username, email, phone } = this.state
    const { organizationName, timezone, address, city, state, zip } = this.state
    const payload: TrialCreateRequest = {}
    payload.type = type
    if (fullName) {
      payload.fullName = fullName
    } else {
      alertService.error('Please add your full name')
      return
    }
    if (role) {
      payload.role = role
    } else {
      alertService.error('Please add your role')
      return
    }
    if (username) {
      payload.username = username
    } else {
      alertService.error('Please add your username')
      return
    }
    if (email) {
      payload.email = email
    } else {
      alertService.error('Please add your email')
      return
    }
    if (phone) payload.phone = phone
    if (organizationName) {
      payload.organizationName = organizationName
    } else {
      alertService.error('Please add your organization name')
      return
    }
    if (timezone) {
      payload.timezone = timezone
    } else {
      alertService.error('Please add your timezone')
      return
    }
    if (address) payload.address = address
    if (city) payload.city = city
    if (state) payload.state = state
    if (zip) payload.zip = zip

    trialApi.create(payload, trial => {
      if (!trial) return
      this.props.history.push(routes.signIn)
      alertService.success('Trial successfully requested. Please check your inbox to verify your email.')
    })
  }

  handleAddressSelect = (addressData: GooglePlaceReadCallbackData) => {
    if (!addressData) return
    const { address, city, state, zip } = addressData
    this.setState({ address, city, state, zip })
  }

  listTimezones () {
    timezoneApi.list(timezones => {
      if (!timezones) return
      this.setState({ timezones })
    })
  }

  componentDidMount () {
    this.listTimezones()
  }

  render () {
    return (
            <Card className='mt-4'>
                <div className='flex justify-between mb-5'>
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Heading
                                text={i18n('Welcome to QPRO+')}
                                className='text-theme'/>
                            {this.props.type === 'customLabel' && <SandboxBadge/>}
                        </div>
                        {this.subHeading}
                        <div className="text-secondary">
                            {i18n('Fill out the form to request for a free account')}
                            <br/>
                            {i18n('No credit card required')}
                        </div>
                    </div>
                    <Logo type='icon'/>
                </div>
                <Row column={2}>
                    <InputText
                        name="fullName"
                        value={this.state.fullName}
                        label={i18n('Full name *')}
                        placeholder="John Smith"
                        onChange={this.handleInputChange}/>
                    <Select
                        name="role"
                        value={this.state.role}
                        options={this.roleSelectOptions}
                        label={i18n('Role *')}
                        onChange={this.handleInputChange}/>
                    <InputText
                        name="username"
                        value={this.state.username}
                        label={i18n('Username *')}
                        onChange={this.handleInputChange}/>
                    <InputText
                        name="email"
                        value={this.state.email}
                        label={i18n('Email *')}
                        onChange={this.handleInputChange}/>
                    <InputText
                        name="phone"
                        value={this.state.phone}
                        label={i18n('Phone')}
                        onChange={this.handleInputChange}/>
                </Row>
                <Heading size='small' text={i18n('Organization')} className='mb-2'/>
                <Row column={2}>
                    <InputText
                        name="organizationName"
                        value={this.state.organizationName}
                        label={i18n('Organization name *')}
                        placeholder={i18n('Your company name or similar')}
                        onChange={this.handleInputChange}/>
                    <Select
                        name="timezone"
                        value={this.state.timezone}
                        options={this.timezoneSelectOptions}
                        label={i18n('Timezone *')}
                        onChange={this.handleInputChange}/>
                    {
                        this.props.type !== 'customLabel' &&
                        <AddressSearchPicker onSelect={this.handleAddressSelect}/>
                    }
                </Row>
                <div className="text-secondary mb-3">
                    {i18n('By clicking the &quot;Request Free Account&quot; button, you acknowledge reading and accepting our')}
                    {' '}
                    <a
                        href="https://www.quandosolutions.com/terms-of-service"
                        className="text-blue-500"
                        target='_blank'
                        rel="noreferrer">{i18n('Terms of service')}</a> {i18n('and our')} &nbsp;
                    <a
                        href="https://www.quandosolutions.com/privacy-policy"
                        className="text-blue-500"
                        target='_blank'
                        rel="noreferrer">{i18n('Privacy Policy')}</a>
                    .
                </div>
                <CardButton
                    proceedText={i18n('Request Free Account')}
                    onProceedClick={this.handleTrialClick}/>
            </Card>
    )
  }
}

export default withRouter(TrialForm)
